import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Send, Search, Mail, Users, CheckCircle2, Clock, MailQuestion, RefreshCw, Sparkles, Eye, Tags, Copy, Check,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import RichTextEditor from "@/components/RichTextEditor";

const TAAL_LABELS = { nl: "Nederlands", en: "English", de: "Deutsch" };

const STATUS_META = {
  actief:      { label: "Actief",       cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: <CheckCircle2 className="h-3 w-3" /> },
  pending:     { label: "In afwachting",cls: "bg-amber-100 text-amber-800 border-amber-200",       icon: <Clock className="h-3 w-3" /> },
  uitgenodigd: { label: "Uitgenodigd",  cls: "bg-sky-100 text-sky-800 border-sky-200",             icon: <MailQuestion className="h-3 w-3" /> },
  geen:        { label: "Geen account", cls: "bg-slate-100 text-slate-600 border-slate-200",       icon: null },
};

function StatBadge({ count, label, tone, testid }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${tone}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-semibold">{count}</div>
    </div>
  );
}

export default function WebshopUitnodigingen() {
  const [tab, setTab] = useState("doelgroep");
  const [klanten, setKlanten] = useState([]);
  const [stats, setStats] = useState({ totaal: 0, actief: 0, pending: 0, uitgenodigd: 0, geen: 0 });
  const [uitnodigingen, setUitnodigingen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLand, setFilterLand] = useState("all");
  const [filterKlantgroep, setFilterKlantgroep] = useState("all");
  const [filterOrgStatus, setFilterOrgStatus] = useState("all");
  const [kenmerkDefs, setKenmerkDefs] = useState([]);
  const [kenmerkenSel, setKenmerkenSel] = useState(() => new Set());
  const [kenmerkenModus, setKenmerkenModus] = useState("any");
  const [selection, setSelection] = useState(() => new Set());
  const [composeOpen, setComposeOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [design, setDesign] = useState(null);
  const [busy, setBusy] = useState(false);
  // Meertalige templates: { nl: {onderwerp, html}, en: {...}, de: {...} }
  const [templates, setTemplates] = useState({
    nl: { onderwerp: "", html: "" },
    en: { onderwerp: "", html: "" },
    de: { onderwerp: "", html: "" },
  });
  const [composeTaal, setComposeTaal] = useState("nl");
  const [previewTaal, setPreviewTaal] = useState("nl");
  const [vervaltDagen, setVervaltDagen] = useState(30);
  const [afzenders, setAfzenders] = useState([]);
  const [afzenderId, setAfzenderId] = useState("");

  const laadAlles = useCallback(async () => {
    setLoading(true);
    try {
      const [k, s, u, d, af] = await Promise.all([
        api.get("/webshop-uitnodigingen/klanten"),
        api.get("/webshop-uitnodigingen/stats"),
        api.get("/webshop-uitnodigingen"),
        api.get("/kenmerk-definities").catch(() => ({ data: [] })),
        api.get("/email/afzenders").catch(() => ({ data: [] })),
      ]);
      setKlanten(k.data || []);
      setStats(s.data || {});
      setUitnodigingen(u.data || []);
      setKenmerkDefs(d.data || []);
      setAfzenders(af.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Laden mislukt");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { laadAlles(); }, [laadAlles]);

  const landen = useMemo(() => {
    const set = new Set();
    klanten.forEach(k => { if (k.land) set.add(k.land); });
    return Array.from(set).sort();
  }, [klanten]);

  const klantgroepen = useMemo(() => {
    const set = new Set();
    klanten.forEach(k => { if (k.klantgroep) set.add(k.klantgroep); });
    return Array.from(set).sort();
  }, [klanten]);

  const orgStatussen = useMemo(() => {
    const set = new Set();
    klanten.forEach(k => { if (k.status) set.add(k.status); });
    return Array.from(set).sort();
  }, [klanten]);

  const toggleKenmerk = (w) => setKenmerkenSel(prev => {
    const nxt = new Set(prev); if (nxt.has(w)) nxt.delete(w); else nxt.add(w); return nxt;
  });

  const gefilterd = useMemo(() => {
    const zoek = q.trim().toLowerCase();
    const sel = kenmerkenSel;
    return klanten.filter(k => {
      if (filterStatus !== "all" && k.webshop_status !== filterStatus) return false;
      if (filterLand !== "all" && (k.land || "") !== filterLand) return false;
      if (filterKlantgroep !== "all" && (k.klantgroep || "") !== filterKlantgroep) return false;
      if (filterOrgStatus !== "all" && (k.status || "") !== filterOrgStatus) return false;
      if (sel.size > 0) {
        const kmSet = new Set((k.kenmerken || []).map(x => x.waarde));
        if (kenmerkenModus === "all") {
          for (const w of sel) if (!kmSet.has(w)) return false;
        } else {
          let ok = false;
          for (const w of sel) if (kmSet.has(w)) { ok = true; break; }
          if (!ok) return false;
        }
      }
      if (zoek) {
        const hay = `${k.naam || ""} ${k.email || ""} ${k.klantcode || ""}`.toLowerCase();
        if (!hay.includes(zoek)) return false;
      }
      return true;
    });
  }, [klanten, q, filterStatus, filterLand, filterKlantgroep, filterOrgStatus, kenmerkenSel, kenmerkenModus]);

  const selecteerbaar = gefilterd.filter(k =>
    k.webshop_status !== "actief" && (k.email || "").trim());

  const allesGekozen = selecteerbaar.length > 0 &&
    selecteerbaar.every(k => selection.has(k.id));

  const toggle = (id) => setSelection(prev => {
    const nxt = new Set(prev);
    if (nxt.has(id)) nxt.delete(id); else nxt.add(id);
    return nxt;
  });

  const toggleAlles = () => {
    if (allesGekozen) {
      setSelection(prev => {
        const nxt = new Set(prev);
        selecteerbaar.forEach(k => nxt.delete(k.id));
        return nxt;
      });
    } else {
      setSelection(prev => {
        const nxt = new Set(prev);
        selecteerbaar.forEach(k => nxt.add(k.id));
        return nxt;
      });
    }
  };

  const openCompose = async () => {
    if (selection.size === 0) { toast.error("Selecteer eerst klanten"); return; }
    try {
      const { data } = await api.get("/webshop-uitnodigingen/template");
      setDesign(data.design || null);
      const nl = data.nl || { onderwerp: "", html: "" };
      const en = data.en || nl;
      const de = data.de || nl;
      setTemplates({
        nl: { onderwerp: nl.onderwerp || "", html: nl.html || "" },
        en: { onderwerp: en.onderwerp || "", html: en.html || "" },
        de: { onderwerp: de.onderwerp || "", html: de.html || "" },
      });
    } catch {
      setTemplates({
        nl: { onderwerp: "Activeer je account", html: "<p>Beste {{organisatie}}, activeer je account via {{activatie_link}}.</p>" },
        en: { onderwerp: "Activate your account", html: "<p>Dear {{organisatie}}, activate your account via {{activatie_link}}.</p>" },
        de: { onderwerp: "Aktivieren Sie Ihr Konto", html: "<p>Guten Tag {{organisatie}}, aktivieren Sie Ihr Konto über {{activatie_link}}.</p>" },
      });
    }
    setComposeTaal("nl");
    setPreviewTaal("nl");
    setComposeOpen(true);
  };

  // Aantal klanten per taal in de huidige selectie — handig om admin te tonen welke taal
  // een default template MOET hebben.
  const selectionPerTaal = useMemo(() => {
    const idSet = selection;
    const tel = { nl: 0, en: 0, de: 0 };
    klanten.forEach(k => { if (idSet.has(k.id)) tel[k.taal || "nl"] = (tel[k.taal || "nl"] || 0) + 1; });
    return tel;
  }, [selection, klanten]);

  const versturen = async () => {
    // Valideer per taal die actief in gebruik is
    const talenNodig = Object.entries(selectionPerTaal).filter(([, n]) => n > 0).map(([t]) => t);
    for (const t of talenNodig.length ? talenNodig : ["nl"]) {
      const tpl = templates[t];
      if (!tpl.onderwerp.trim() || (tpl.html || "").length < 30) {
        toast.error(`${TAAL_LABELS[t]}: onderwerp en boodschap zijn verplicht`); return;
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/webshop-uitnodigingen/bulk", {
        organisatie_ids: Array.from(selection),
        templates: {
          nl: { onderwerp: templates.nl.onderwerp, boodschap_html: templates.nl.html },
          en: { onderwerp: templates.en.onderwerp, boodschap_html: templates.en.html },
          de: { onderwerp: templates.de.onderwerp, boodschap_html: templates.de.html },
        },
        afzender_id: afzenderId || null,
        vervalt_dagen: Number(vervaltDagen) || 30,
      });
      toast.success(`Verzonden: ${data.verzonden}. Overgeslagen: ${data.overgeslagen}.`);
      if ((data.fouten || []).length) console.warn("Uitnodigingen met fouten:", data.fouten);
      setComposeOpen(false);
      setSelection(new Set());
      laadAlles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versturen mislukt");
    } finally { setBusy(false); }
  };

  const [gekopieerd, setGekopieerd] = useState(null);
  const kopieerLink = async (u) => {
    const link = u.activatie_link || `${window.location.origin}/webshop-activeren/${u.token}`;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(link);
      else { const ta = document.createElement("textarea"); ta.value = link; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove(); }
      setGekopieerd(u.id); setTimeout(() => setGekopieerd(null), 2500);
      toast.success("Activatielink gekopieerd — plak hem in WhatsApp of een mail");
    } catch { window.prompt("Kopieer de activatielink:", link); }
  };

  const opnieuwVerzenden = async (uid) => {
    try {
      await api.post(`/webshop-uitnodigingen/${uid}/opnieuw`);
      toast.success("Uitnodiging opnieuw verstuurd");
      laadAlles();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mislukt"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-5" data-testid="webshop-uitnodigingen-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">Webshop-uitnodigingen</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Nodig klanten uit om hun account op het bestelportaal te activeren. Bij activatie
            worden ontbrekende bedrijfsgegevens (KvK, BTW, adres) automatisch opgevraagd.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={laadAlles} data-testid="btn-refresh">
            <RefreshCw className="h-4 w-4 mr-2" />Verversen
          </Button>
          <Button onClick={openCompose} disabled={selection.size === 0} data-testid="btn-open-compose">
            <Send className="h-4 w-4 mr-2" />Uitnodigen ({selection.size})
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <StatBadge testid="stat-totaal"      count={stats.totaal || 0}      label="Totaal klanten" tone="bg-white" />
        <StatBadge testid="stat-actief"      count={stats.actief || 0}      label="Actief"         tone="bg-emerald-50 border-emerald-200 text-emerald-900" />
        <StatBadge testid="stat-pending"     count={stats.pending || 0}     label="In afwachting"  tone="bg-amber-50 border-amber-200 text-amber-900" />
        <StatBadge testid="stat-uitgenodigd" count={stats.uitgenodigd || 0} label="Uitgenodigd"    tone="bg-sky-50 border-sky-200 text-sky-900" />
        <StatBadge testid="stat-geen"        count={stats.geen || 0}        label="Geen account"   tone="bg-slate-50 border-slate-200 text-slate-800" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="doelgroep" data-testid="tab-doelgroep">
            <Users className="h-4 w-4 mr-1.5" />Doelgroep
          </TabsTrigger>
          <TabsTrigger value="verstuurd" data-testid="tab-verstuurd">
            <Mail className="h-4 w-4 mr-1.5" />Verstuurd ({uitnodigingen.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="doelgroep" className="space-y-3 pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Zoek naam / e-mail / klantcode"
                className="pl-8 w-72" data-testid="filter-zoek" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statussen</SelectItem>
                <SelectItem value="geen">Geen account</SelectItem>
                <SelectItem value="uitgenodigd">Uitgenodigd</SelectItem>
                <SelectItem value="pending">In afwachting</SelectItem>
                <SelectItem value="actief">Actief</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterLand} onValueChange={setFilterLand}>
              <SelectTrigger className="w-40" data-testid="filter-land"><SelectValue placeholder="Land" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle landen</SelectItem>
                {landen.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterKlantgroep} onValueChange={setFilterKlantgroep}>
              <SelectTrigger className="w-44" data-testid="filter-klantgroep"><SelectValue placeholder="Klantgroep" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle klantgroepen</SelectItem>
                {klantgroepen.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterOrgStatus} onValueChange={setFilterOrgStatus}>
              <SelectTrigger className="w-44" data-testid="filter-org-status"><SelectValue placeholder="Klant-status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle klant-statussen</SelectItem>
                {orgStatussen.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground ml-auto">
              {gefilterd.length} klanten • {selection.size} geselecteerd
            </div>
          </div>

          {kenmerkDefs.length > 0 && (
            <div data-testid="kenmerken-filter" className="border rounded-lg p-3 space-y-2 bg-muted/20">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <Tags className="h-3.5 w-3.5" /> Kenmerken
                  {kenmerkenSel.size > 0 && <span className="text-primary">({kenmerkenSel.size} gekozen)</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Match:</span>
                  <Select value={kenmerkenModus} onValueChange={setKenmerkenModus}>
                    <SelectTrigger className="h-7 w-[180px] text-xs" data-testid="kenmerken-modus"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Minimaal één (ANY)</SelectItem>
                      <SelectItem value="all">Alle (ALL)</SelectItem>
                    </SelectContent>
                  </Select>
                  {kenmerkenSel.size > 0 && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs"
                      onClick={() => setKenmerkenSel(new Set())} data-testid="kenmerken-reset">
                      Wis
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {kenmerkDefs.map((d) => ((d.waarden || []).length > 0 ? (
                  <div key={d.id || d.categorie}>
                    <div className="text-[11px] font-medium text-foreground/70 mb-1">{d.categorie}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {d.waarden.map((w) => {
                        const actief = kenmerkenSel.has(w);
                        return (
                          <button key={w} type="button" onClick={() => toggleKenmerk(w)}
                            data-testid={`kenmerk-${w}`}
                            className={`px-2 py-0.5 rounded-full text-[11px] border transition ${actief
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background border-border hover:bg-muted"}`}>
                            {w}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null))}
              </div>
            </div>
          )}

          <div className="border rounded-lg overflow-hidden bg-background">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs text-muted-foreground">
                <tr>
                  <th className="w-10 px-3 py-2 text-left">
                    <Checkbox checked={allesGekozen} onCheckedChange={toggleAlles} data-testid="chk-alles" />
                  </th>
                  <th className="px-3 py-2 text-left">Organisatie</th>
                  <th className="px-3 py-2 text-left">Klantcode</th>
                  <th className="px-3 py-2 text-left">E-mail</th>
                  <th className="px-3 py-2 text-left">Land</th>
                  <th className="px-3 py-2 text-left">Klantgroep</th>
                  <th className="px-3 py-2 text-left">Klant-status</th>
                  <th className="px-3 py-2 text-left">Taal</th>
                  <th className="px-3 py-2 text-left">Webshop-status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">Laden…</td></tr>
                )}
                {!loading && gefilterd.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">Geen klanten die aan de filter voldoen.</td></tr>
                )}
                {gefilterd.map(k => {
                  const meta = STATUS_META[k.webshop_status] || STATUS_META.geen;
                  const uitschakelen = k.webshop_status === "actief" || !(k.email || "").trim();
                  return (
                    <tr key={k.id} className="border-t hover:bg-muted/30" data-testid={`row-${k.id}`}>
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={selection.has(k.id)}
                          disabled={uitschakelen}
                          onCheckedChange={() => toggle(k.id)}
                          data-testid={`chk-${k.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium">{k.naam || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{k.klantcode || "—"}</td>
                      <td className="px-3 py-2">
                        {k.email || <span className="text-red-500 text-xs">geen e-mail</span>}
                      </td>
                      <td className="px-3 py-2">{k.land || "—"}</td>
                      <td className="px-3 py-2 text-xs">{k.klantgroep || "—"}</td>
                      <td className="px-3 py-2 text-xs">{k.status || "—"}</td>
                      <td className="px-3 py-2 text-xs font-mono uppercase">{k.taal || "nl"}</td>
                      <td className="px-3 py-2">
                        <Badge className={`${meta.cls} border font-normal gap-1`}>
                          {meta.icon}{meta.label}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="verstuurd" className="pt-3">
          <div className="border rounded-lg overflow-hidden bg-background">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Organisatie</th>
                  <th className="px-3 py-2 text-left">E-mail</th>
                  <th className="px-3 py-2 text-left">Onderwerp</th>
                  <th className="px-3 py-2 text-left">Verstuurd</th>
                  <th className="px-3 py-2 text-left">Verloopt</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Actie</th>
                </tr>
              </thead>
              <tbody>
                {uitnodigingen.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                    Nog geen uitnodigingen verstuurd.
                  </td></tr>
                )}
                {uitnodigingen.map(u => {
                  const fmt = (d) => d ? new Date(d).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }) : "—";
                  const geactiveerd = u.status === "geactiveerd";
                  return (
                    <tr key={u.id} className="border-t" data-testid={`inv-row-${u.id}`}>
                      <td className="px-3 py-2 font-medium">{u.organisatie_naam || "—"}</td>
                      <td className="px-3 py-2">{u.email}</td>
                      <td className="px-3 py-2 text-xs">{u.onderwerp || "—"}</td>
                      <td className="px-3 py-2 text-xs">{fmt(u.sent_at)}</td>
                      <td className="px-3 py-2 text-xs">{fmt(u.expires_at)}</td>
                      <td className="px-3 py-2">
                        <Badge className={geactiveerd
                          ? "bg-emerald-100 text-emerald-800 border border-emerald-200 font-normal"
                          : "bg-sky-100 text-sky-800 border border-sky-200 font-normal"}>
                          {geactiveerd ? "Geactiveerd" : "Verstuurd"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {!geactiveerd && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => kopieerLink(u)} title={u.activatie_link}
                              data-testid={`btn-kopieer-link-${u.id}`} className="h-7 text-xs">
                              {gekopieerd === u.id ? <Check className="h-3.5 w-3.5 mr-1 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                              {gekopieerd === u.id ? "Gekopieerd" : "Link kopiëren"}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => opnieuwVerzenden(u.id)}
                              data-testid={`btn-opnieuw-${u.id}`} className="h-7 text-xs">
                              <RefreshCw className="h-3.5 w-3.5 mr-1" />Opnieuw
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="compose-dialog">
          <DialogHeader>
            <DialogTitle>Uitnodigingsmail opstellen — {selection.size} klant(en)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground bg-muted/50 border rounded px-3 py-2">
              Elke klant ontvangt de mail in zijn eigen taal (op basis van land). In deze selectie:
              {" "}
              <strong>NL {selectionPerTaal.nl}</strong> · <strong>EN {selectionPerTaal.en}</strong> · <strong>DE {selectionPerTaal.de}</strong>.
              Onbekende talen vallen terug op Nederlands.
            </div>
            {design ? (
              <div className="text-xs bg-primary/5 border border-primary/20 rounded px-3 py-2" data-testid="compose-design-notice">
                Verstuurd in het huisstijl-ontwerp <strong>{design.naam}</strong>: logo, activatieknop, link en klantcode zitten al in het ontwerp.
                Je boodschap hieronder wordt in het ontwerp geplaatst (op <code>{"{{boodschap}}"}</code>). Ontwerp aanpassen: <Link to="/instellingen/email-ontwerpen" className="underline">Instellingen → E-mailontwerpen</Link>.
              </div>
            ) : (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-900" data-testid="compose-design-notice">
                Geen standaard huisstijl-ontwerp voor uitnodigingen — de mail wordt verstuurd zoals je hem hieronder opstelt (incl. <code>{"{{activatie_link}}"}</code>).
              </div>
            )}
            <Tabs value={composeTaal} onValueChange={setComposeTaal}>
              <TabsList>
                {["nl", "en", "de"].map(t => (
                  <TabsTrigger key={t} value={t} data-testid={`tab-taal-${t}`}>
                    {TAAL_LABELS[t]}
                    {selectionPerTaal[t] > 0 && (
                      <Badge className="ml-2 h-4 px-1.5 text-[10px] font-normal bg-primary/10 text-primary border-0">
                        {selectionPerTaal[t]}
                      </Badge>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
              {["nl", "en", "de"].map(t => (
                <TabsContent key={t} value={t} className="space-y-3 pt-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Onderwerp ({TAAL_LABELS[t]})</label>
                    <Input
                      value={templates[t].onderwerp}
                      onChange={e => setTemplates(p => ({ ...p, [t]: { ...p[t], onderwerp: e.target.value } }))}
                      data-testid={`input-onderwerp-${t}`} placeholder="…" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      Boodschap (gebruik <code>{"{{organisatie}}"}</code>, <code>{"{{klantcode}}"}</code>, <code>{"{{activatie_link}}"}</code>)
                    </label>
                    <RichTextEditor
                      value={templates[t].html}
                      onChange={(v) => setTemplates(p => ({ ...p, [t]: { ...p[t], html: v } }))}
                      testid={`rte-uitnodiging-${t}`} />
                  </div>
                </TabsContent>
              ))}
            </Tabs>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-xs font-medium text-muted-foreground">Afzender</label>
              <Select value={afzenderId || "_default"} onValueChange={(v) => setAfzenderId(v === "_default" ? "" : v)}>
                <SelectTrigger className="w-72 h-9" data-testid="input-afzender"><SelectValue placeholder="Kies afzender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_default">Standaard (facturatie-afzender)</SelectItem>
                  {afzenders.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.naam} — {a.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="text-xs font-medium text-muted-foreground ml-4">Vervalt na (dagen)</label>
              <Input type="number" min="1" max="365" value={vervaltDagen}
                onChange={e => setVervaltDagen(e.target.value)}
                className="w-24" data-testid="input-vervalt-dagen" />
              <div className="flex-1" />
              <Button variant="outline" onClick={() => { setPreviewTaal(composeTaal); setPreviewOpen(true); }} data-testid="btn-preview">
                <Eye className="h-4 w-4 mr-2" />Voorbeeld ({TAAL_LABELS[composeTaal]})
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)} disabled={busy}>Annuleren</Button>
            <Button onClick={versturen} disabled={busy} data-testid="btn-versturen">
              <Send className="h-4 w-4 mr-2" />
              {busy ? "Versturen…" : `Verstuur naar ${selection.size} klant(en)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader><DialogTitle>Voorbeeld — {TAAL_LABELS[previewTaal]}</DialogTitle></DialogHeader>
          {design ? <DesignPreview taal={previewTaal} tmpl={templates[previewTaal]} /> : <div className="border rounded-lg p-4 bg-white">
            <div className="text-xs text-muted-foreground mb-2">
              Onderwerp: <strong>{(templates[previewTaal]?.onderwerp || "")
                .replaceAll("{{organisatie}}", "Voorbeeld B.V.")
                .replaceAll("{{klantcode}}", "NLAP0001")}</strong>
            </div>
            <div className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{
                __html: (templates[previewTaal]?.html || "")
                  .replaceAll("{{organisatie}}", "Voorbeeld B.V.")
                  .replaceAll("{{klantcode}}", "NLAP0001")
                  .replaceAll("{{activatie_link}}", "#voorbeeld-link")
                  .replaceAll("{{afzender_naam}}", "Jouw bedrijf"),
              }}
            />
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DesignPreview({ taal, tmpl }) {
  const [res, setRes] = useState(null);
  useEffect(() => {
    setRes(null);
    api.post("/webshop-uitnodigingen/preview", { taal, onderwerp: tmpl?.onderwerp || "", boodschap_html: tmpl?.html || "" })
      .then(r => setRes(r.data)).catch(() => setRes({ onderwerp: "", html: "<p>Voorbeeld kon niet worden geladen.</p>" }));
  }, [taal, tmpl?.onderwerp, tmpl?.html]);
  if (!res) return <div className="text-sm text-muted-foreground p-4">Voorbeeld laden…</div>;
  return (
    <div className="space-y-2" data-testid="preview-design">
      <div className="text-xs text-muted-foreground">Onderwerp: <strong data-testid="preview-design-onderwerp">{res.onderwerp}</strong></div>
      <iframe title="voorbeeld" srcDoc={res.html} className="w-full rounded-lg border bg-white" style={{ height: "70vh" }} data-testid="preview-design-frame" />
    </div>
  );
}
